import React, { useEffect, useMemo, useState } from 'react'
import styles from './Filters.module.css'
import { FilterSchema, FilterSchemaValue } from '../../../../services/api'
import type { Product } from '../../../../types/product'
import { motion } from 'framer-motion'
import { getMinEffectivePrice } from '../../../../utils/cartUtils'

type DynamicFiltersState = Record<string, string[] | [number, number]>

interface FiltersProps {
  schema?: FilterSchema
  initialFilters?: DynamicFiltersState
  categoryOptions?: { value: string; label: string }[]
  initialCategory?: string | null
  onCategoryChange?: (category: string | null) => void
  onFiltersChange?: (filters: DynamicFiltersState) => void
  onClose?: () => void
  isOpen?: boolean
  className?: string
  products?: Product[]
}

export default function Filters({
  schema,
  initialFilters,
  categoryOptions,
  initialCategory = null,
  onCategoryChange,
  onFiltersChange,
  onClose,
  isOpen = false,
  className = '',
  products = [],
}: FiltersProps) {
  const isValuesField = (def: unknown): def is { values: { value: string; label: string }[] } => {
    return (
      !!def &&
      typeof def === 'object' &&
      def !== null &&
      'values' in def &&
      Array.isArray((def as { values: unknown }).values)
    )
  }

  const isRangeField = (def: unknown): def is { from: number; to: number } => {
    return (
      !!def &&
      typeof def === 'object' &&
      def !== null &&
      'from' in def &&
      'to' in def &&
      typeof (def as { from: unknown; to: unknown }).from === 'number' &&
      typeof (def as { from: unknown; to: unknown }).to === 'number'
    )
  }

  const initialState = useMemo<DynamicFiltersState>(() => {
    const state: DynamicFiltersState = {}
    if (schema) {
      Object.entries(schema).forEach(([key, def]) => {
        if (isValuesField(def)) {
          state[key] = []
        } else if (isRangeField(def)) {
          state[key] = [def.from, def.to]
        }
      })
    }
    return state
  }, [schema])

  const [filters, setFilters] = useState<DynamicFiltersState>(initialState)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(initialCategory)
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    const exp: Record<string, boolean> = {}
    if (schema) {
      Object.entries(schema).forEach(([key, def]) => {
        if (isValuesField(def) || isRangeField(def)) {
          exp[key] = true
        }
      })
    }
    if (categoryOptions && categoryOptions.length > 0) {
      exp['productType'] = true
    }
    if ((products || []).length > 0) {
      exp['price'] = true
    }
    // Stickers (tags) section opened by default
    exp['tags'] = true
    return exp
  })

  // Reset filters when schema changes
  useEffect(() => {
    // Merge initial filters if provided
    if (initialFilters) {
      const merged: DynamicFiltersState = { ...initialState }
      Object.entries(initialFilters).forEach(([k, v]) => {
        merged[k] = v
      })
      setFilters(merged)
    } else {
      setFilters(initialState)
    }
    setSelectedCategory(initialCategory)
    // Ensure expanded is defined for valid fields
    const exp: Record<string, boolean> = {}
    if (schema) {
      Object.entries(schema).forEach(([key, def]) => {
        if (isValuesField(def) || isRangeField(def)) {
          exp[key] = true
        }
      })
    }
    if (categoryOptions && categoryOptions.length > 0) {
      exp['productType'] = true
    }
    if ((products || []).length > 0) {
      exp['price'] = true
    }
    exp['tags'] = true
    setExpanded(exp)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialState, schema, initialFilters, initialCategory])

  const toggleSection = (section: string) => {
    setExpanded(prev => ({ ...prev, [section]: !prev[section] }))
  }

  const handleListToggle = (key: string, value: string) => {
    setFilters(prev => {
      const current = (prev[key] as string[]) || []
      const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value]
      return { ...prev, [key]: next }
    })
  }

  const handleRangeChange = (key: string, min: number, max: number) => {
    setFilters(prev => ({ ...prev, [key]: [min, max] }))
  }

  const handleRemoveValue = (key: string, value: string) => {
    setFilters(prev => {
      const list = ((prev[key] as string[]) || []).filter(v => v !== value)
      return { ...prev, [key]: list }
    })
  }

  const handleResetRange = (key: string, from: number, to: number) => {
    setFilters(prev => ({ ...prev, [key]: [from, to] }))
  }

  const appliedChips = () => {
    const chips: { id: string; label: string; onRemove: () => void }[] = []
    if (!schema) return chips

    Object.entries(schema).forEach(([key, def]) => {
      const current = filters[key]
      const title = 'name' in def ? (def as { name: string }).name : key

      // Values list
      if ('values' in def) {
        const selected: string[] = Array.isArray(current) ? (current as string[]) : []
        selected.forEach(val => {
          const opt = (def as { values: FilterSchemaValue[] }).values.find(o => o.value === val)
          const text = opt?.label || val
          chips.push({
            id: `${key}:${val}`,
            label: text,
            onRemove: () => handleRemoveValue(key, val),
          })
        })
      }

      // Range
      if ('from' in def && Array.isArray(current)) {
        const [min, max] = current as [number, number]
        // For price, derive default range from products
        const prices = (products || [])
          .map(p => getMinEffectivePrice(p.prices || []))
          .filter((n): n is number => n !== null)
        const priceFrom = prices.length ? Math.min(...prices) : (def as { from: number }).from
        const priceTo = prices.length ? Math.max(...prices) : (def as { to: number }).to
        const from = key === 'price' ? priceFrom : (def as { from: number }).from
        const to = key === 'price' ? priceTo : (def as { to: number }).to
        if (min !== from || max !== to) {
          let text = ''
          if (key === 'price') {
            text = `${min}₴–${max}₴`
          } else if (['sweetness', 'acidity', 'bitterness', 'scaScore'].includes(key)) {
            if (max === to) text = `${title} ${min}/${to}`
            else text = `${title} ${min}–${max}`
          } else {
            text = `${title} ${min}–${max}`
          }
          chips.push({
            id: `${key}:range`,
            label: text,
            onRemove: () => handleResetRange(key, from, to),
          })
        }
      }
    })

    // Custom tags (markers)
    const tags = (filters['tags'] as string[]) || []
    tags.forEach(t =>
      chips.push({ id: `tags:${t}`, label: t, onRemove: () => handleRemoveValue('tags', t) }),
    )

    return chips
  }

  const resetFilters = () => {
    setFilters(initialState)
    setSelectedCategory(null)
    onFiltersChange?.(initialState)
    onCategoryChange?.(null)
  }

  const getActiveFiltersCount = () => {
    const base = Object.entries(filters).reduce((acc, [key, val]) => {
      if (Array.isArray(val)) {
        // Range or list
        const schemaDef = schema ? schema[key] : undefined
        if (isRangeField(schemaDef)) {
          const [min, max] = val as [number, number]
          return acc + (min !== schemaDef.from || max !== schemaDef.to ? 1 : 0)
        }
        return acc + ((val as string[]).length > 0 ? 1 : 0)
      }
      return acc
    }, 0)
    return base + (selectedCategory ? 1 : 0)
  }

  const handleApply = () => {
    onFiltersChange?.(filters)
    onCategoryChange?.(selectedCategory)
    onClose?.()
  }

  if (!isOpen) return null

  return (
    <motion.div
      initial={{ opacity: 0, y: 100 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 100 }}
      transition={{ duration: 0.3 }}
      className={`${styles.filters} ${className}`}
    >
      <div className={styles.header}>
        <h2 className={styles.title}>Фільтри</h2>
        <button className={styles.closeButton} onClick={onClose} aria-label="Закрити фільтри">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 22 22"
            fill="none"
          >
            <rect x="1" y="1" width="20" height="20" rx="6" stroke="#BFBFBF" strokeWidth="1.25" />
            <path
              d="M8.87845 13.1215L13.1211 8.87891"
              stroke="#BFBFBF"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8.87845 8.87894L13.1211 13.1216"
              stroke="#BFBFBF"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <button className={styles.resetButton} onClick={resetFilters}>
        Скинути всі фільтри
      </button>

      {appliedChips().length > 0 && (
        <div className={styles.appliedFilters}>
          {appliedChips().map(chip => (
            <span key={chip.id} className={styles.filterTag}>
              {chip.label}
              <button onClick={chip.onRemove}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <rect
                    x="1.33398"
                    y="1.3333"
                    width="13.3333"
                    height="13.3333"
                    rx="4"
                    stroke="#BFBFBF"
                    strokeWidth="1.25"
                  />
                  <path
                    d="M6.58564 9.41435L9.41406 6.58592"
                    stroke="#BFBFBF"
                    strokeWidth="1.25"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6.58563 6.58593L9.41406 9.41435"
                    stroke="#BFBFBF"
                    strokeWidth="1.25"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      <div className={styles.filtersList}>
        {/* Stickers (tags) - HIDDEN */}
        {/* <div className={styles.filterSection}>
          <button className={styles.sectionHeader} onClick={() => toggleSection('tags')}>
            <span>Стікер</span>
            <svg
              className={`${styles.chevron} ${expanded['tags'] ? styles.expanded : ''}`}
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="8"
              viewBox="0 0 14 8"
              fill="none"
            >
              <path
                d="M14 6.99982V6.99982C10.1372 6.99982 7.00092 3.86288 7.01128 7.01006e-05V7.01006e-05"
                stroke="#0D0D0D"
                strokeWidth="1.5"
              />
              <path
                d="M0 7.00006V7.00006C3.86283 7.00006 6.99908 3.86313 6.98872 0.000314585V0.000314585"
                stroke="#0D0D0D"
                strokeWidth="1.5"
              />
            </svg>
          </button>
          {expanded['tags'] && (
            <div className={styles.sectionContent}>
              {[
                { value: 'новинка', label: 'Новинка' },
                { value: 'bestseller', label: 'Bestseller' },
                { value: 'знижка', label: 'Знижка' },
              ].map(opt => (
                <label key={opt.value} className={styles.toggleLabel}>
                  <input
                    type="checkbox"
                    checked={((filters['tags'] as string[]) || []).includes(opt.value)}
                    onChange={() => handleListToggle('tags', opt.value)}
                    className={styles.toggleInput}
                  />
                  <span className={styles.toggleSwitch}></span>
                  {opt.label}
                </label>
              ))}
            </div>
          )}
        </div> */}
        {categoryOptions && categoryOptions.length > 0 && (
          <div className={styles.filterSection}>
            <button className={styles.sectionHeader} onClick={() => toggleSection('productType')}>
              <span>Тип товару</span>
              <svg
                className={`${styles.chevron} ${expanded['productType'] ? styles.expanded : ''}`}
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="8"
                viewBox="0 0 14 8"
                fill="none"
              >
                <path
                  d="M14 6.99982V6.99982C10.1372 6.99982 7.00092 3.86288 7.01128 7.01006e-05V7.01006e-05"
                  stroke="#0D0D0D"
                  strokeWidth="1.5"
                />
                <path
                  d="M0 7.00006V7.00006C3.86283 7.00006 6.99908 3.86313 6.98872 0.000314585V0.000314585"
                  stroke="#0D0D0D"
                  strokeWidth="1.5"
                />
              </svg>
            </button>
            {expanded['productType'] && (
              <div className={styles.sectionContent}>
                {categoryOptions.map(opt => (
                  <label key={opt.value} className={styles.checkboxLabel}>
                    <input
                      type="radio"
                      name="productTypeCategory"
                      checked={selectedCategory === opt.value}
                      onChange={() => setSelectedCategory(opt.value)}
                      className={styles.checkbox}
                    />
                    <span className={styles.checkboxCustom}></span>
                    {opt.label}
                  </label>
                ))}
                <label key="none" className={styles.checkboxLabel}>
                  <input
                    type="radio"
                    name="productTypeCategory"
                    checked={selectedCategory === null}
                    onChange={() => setSelectedCategory(null)}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxCustom}></span>
                  Усі
                </label>
              </div>
            )}
          </div>
        )}

        {products &&
          products.length > 0 &&
          (() => {
            const prices = (products || [])
              .map(p => getMinEffectivePrice(p.prices || []))
              .filter((n): n is number => n !== null)
            if (prices.length === 0) return null
            const rangeMin = Math.min(...prices)
            const rangeMax = Math.max(...prices)
            const cur = ((filters['price'] as [number, number]) || [rangeMin, rangeMax]) as [
              number,
              number,
            ]
            const currentMin = cur[0]
            const currentMax = cur[1]
            const toPercent = (val: number) => ((val - rangeMin) / (rangeMax - rangeMin)) * 100
            const leftPercent = toPercent(currentMin)
            const rightPercent = 100 - toPercent(currentMax)
            const leftClipPath = `inset(0 ${rightPercent}% 0 0)`
            const rightClipPath = `inset(0 0 0 ${leftPercent}%)`

            return (
              <div className={styles.filterSection}>
                <button className={styles.sectionHeader} onClick={() => toggleSection('price')}>
                  <span>Ціна</span>
                  <svg
                    className={`${styles.chevron} ${expanded['price'] ? styles.expanded : ''}`}
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="8"
                    viewBox="0 0 14 8"
                    fill="none"
                  >
                    <path
                      d="M14 6.99982V6.99982C10.1372 6.99982 7.00092 3.86288 7.01128 7.01006e-05V7.01006e-05"
                      stroke="#0D0D0D"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M0 7.00006V7.00006C3.86283 7.00006 6.99908 3.86313 6.98872 0.000314585V0.000314585"
                      stroke="#0D0D0D"
                      strokeWidth="1.5"
                    />
                  </svg>
                </button>
                {expanded['price'] && (
                  <div className={styles.sectionContent}>
                    <div className={styles.priceSlider}>
                      <div className={styles.sliderBase}></div>
                      <div
                        className={styles.sliderRange}
                        style={{ left: `${leftPercent}%`, right: `${rightPercent}%` }}
                      ></div>
                      <input
                        type="range"
                        min={rangeMin}
                        max={rangeMax}
                        value={currentMin}
                        onChange={e =>
                          handleRangeChange('price', Number(e.target.value), currentMax)
                        }
                        className={`${styles.rangeInput} ${styles.rangeInputLeft}`}
                        style={{
                          clipPath: leftClipPath,
                          WebkitClipPath: leftClipPath,
                        }}
                      />
                      <input
                        type="range"
                        min={rangeMin}
                        max={rangeMax}
                        value={currentMax}
                        onChange={e =>
                          handleRangeChange('price', currentMin, Number(e.target.value))
                        }
                        className={`${styles.rangeInput} ${styles.rangeInputRight}`}
                        style={{
                          clipPath: rightClipPath,
                          WebkitClipPath: rightClipPath,
                        }}
                      />
                    </div>
                    <div className={styles.priceInputs}>
                      <input
                        type="number"
                        value={currentMin}
                        onChange={e =>
                          handleRangeChange('price', Number(e.target.value), currentMax)
                        }
                        className={styles.priceInput}
                      />
                      <input
                        type="number"
                        value={currentMax}
                        onChange={e =>
                          handleRangeChange('price', currentMin, Number(e.target.value))
                        }
                        className={styles.priceInput}
                      />
                    </div>
                  </div>
                )}
              </div>
            )
          })()}

        {schema &&
          Object.entries(schema).map(([key, def]) => {
            const isValues = isValuesField(def)
            const isRange = isRangeField(def)
            if (!isValues && !isRange) return null
            // Precompute positions for range slider fill
            let rangeMin = 0
            let rangeMax = 0
            let currentMin = 0
            let currentMax = 0
            let leftPercent = 0
            let rightPercent = 0
            if (isRange) {
              rangeMin = def.from
              rangeMax = def.to
              const cur = ((filters[key] as [number, number]) || [def.from, def.to]) as [
                number,
                number,
              ]
              currentMin = cur[0]
              currentMax = cur[1]
              const toPercent = (val: number) => ((val - rangeMin) / (rangeMax - rangeMin)) * 100
              leftPercent = toPercent(currentMin)
              rightPercent = 100 - toPercent(currentMax)
            }

            const leftClipPath = `inset(0 ${rightPercent}% 0 0)`
            const rightClipPath = `inset(0 0 0 ${leftPercent}%)`

            const sectionTitle = 'name' in def ? (def as { name: string }).name : key

            return (
              <div className={styles.filterSection} key={key}>
                <button className={styles.sectionHeader} onClick={() => toggleSection(key)}>
                  <span>{sectionTitle}</span>
                  <svg
                    className={`${styles.chevron} ${expanded[key] ? styles.expanded : ''}`}
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="8"
                    viewBox="0 0 14 8"
                    fill="none"
                  >
                    <path
                      d="M14 6.99982V6.99982C10.1372 6.99982 7.00092 3.86288 7.01128 7.01006e-05V7.01006e-05"
                      stroke="#0D0D0D"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M0 7.00006V7.00006C3.86283 7.00006 6.99908 3.86313 6.98872 0.000314585V0.000314585"
                      stroke="#0D0D0D"
                      strokeWidth="1.5"
                    />
                  </svg>
                </button>
                {expanded[key] && (
                  <div className={styles.sectionContent}>
                    {isValues ? (
                      (def.values || []).map(option => (
                        <label key={option.value} className={styles.checkboxLabel}>
                          <input
                            type="checkbox"
                            checked={((filters[key] as string[]) || []).includes(option.value)}
                            onChange={() => handleListToggle(key, option.value)}
                            className={styles.checkbox}
                          />
                          <span className={styles.checkboxCustom}></span>
                          {option.label}
                        </label>
                      ))
                    ) : (
                      <div className={styles.sectionContent}>
                        <div className={styles.priceSlider}>
                          <div className={styles.sliderBase}></div>
                          {isRange && (
                            <div
                              className={styles.sliderRange}
                              style={{ left: `${leftPercent}%`, right: `${rightPercent}%` }}
                            ></div>
                          )}
                          <input
                            type="range"
                            min={isRange ? def.from : 0}
                            max={isRange ? def.to : 0}
                            value={
                              ((filters[key] as [number, number]) || [
                                isRange ? def.from : 0,
                                isRange ? def.to : 0,
                              ])[0]
                            }
                            onChange={e =>
                              handleRangeChange(
                                key,
                                Number(e.target.value),
                                ((filters[key] as [number, number]) || [
                                  isRange ? def.from : 0,
                                  isRange ? def.to : 0,
                                ])[1],
                              )
                            }
                            className={`${styles.rangeInput} ${styles.rangeInputLeft}`}
                            style={{
                              clipPath: leftClipPath,
                              WebkitClipPath: leftClipPath,
                            }}
                          />
                          <input
                            type="range"
                            min={isRange ? def.from : 0}
                            max={isRange ? def.to : 0}
                            value={
                              ((filters[key] as [number, number]) || [
                                isRange ? def.from : 0,
                                isRange ? def.to : 0,
                              ])[1]
                            }
                            onChange={e =>
                              handleRangeChange(
                                key,
                                ((filters[key] as [number, number]) || [
                                  isRange ? def.from : 0,
                                  isRange ? def.to : 0,
                                ])[0],
                                Number(e.target.value),
                              )
                            }
                            className={`${styles.rangeInput} ${styles.rangeInputRight}`}
                            style={{
                              clipPath: rightClipPath,
                              WebkitClipPath: rightClipPath,
                            }}
                          />
                        </div>
                        <div className={styles.priceInputs}>
                          <input
                            type="number"
                            value={
                              ((filters[key] as [number, number]) || [
                                isRange ? def.from : 0,
                                isRange ? def.to : 0,
                              ])[0]
                            }
                            onChange={e =>
                              handleRangeChange(
                                key,
                                Number(e.target.value),
                                ((filters[key] as [number, number]) || [
                                  isRange ? def.from : 0,
                                  isRange ? def.to : 0,
                                ])[1],
                              )
                            }
                            className={styles.priceInput}
                          />
                          <input
                            type="number"
                            value={
                              ((filters[key] as [number, number]) || [
                                isRange ? def.from : 0,
                                isRange ? def.to : 0,
                              ])[1]
                            }
                            onChange={e =>
                              handleRangeChange(
                                key,
                                ((filters[key] as [number, number]) || [
                                  isRange ? def.from : 0,
                                  isRange ? def.to : 0,
                                ])[0],
                                Number(e.target.value),
                              )
                            }
                            className={styles.priceInput}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
      </div>

      <div className={styles.footer}>
        <button className={styles.applyButton} onClick={handleApply}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
          >
            <path
              d="M7.66797 0.168823V0.168823C7.66797 4.03165 10.8049 7.16791 14.6677 7.15755V7.15755"
              stroke="white"
              strokeWidth="1.5"
            />
            <path
              d="M7.66797 14.166V14.166C7.66797 10.3032 10.8049 7.16693 14.6677 7.17729V7.17729"
              stroke="white"
              strokeWidth="1.5"
            />
            <path d="M12.334 7.16602L0.333984 7.16602" stroke="white" strokeWidth="1.5" />
          </svg>
          <span>
            Застосувати <span className={styles.count}>({getActiveFiltersCount()})</span>
          </span>
        </button>
      </div>
    </motion.div>
  )
}
